// Portal API client for /api/portal/*.
// Auth token persisted in localStorage under "tf_portal_token".
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "tf_portal_token";
const USER_KEY = "tf_portal_user";

// Firefox throws "The operation is insecure" when localStorage is gated
// (private browsing strict mode, cookies blocked, etc.). Wrap every access
// in a defensive try/catch — the portal must still load and surface the
// login form rather than crash into the error boundary.
const _safeGet = (k) => {
  try { return localStorage.getItem(k); } catch { return null; }
};
const _safeSet = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* storage disabled */ }
};
const _safeRemove = (k) => {
  try { localStorage.removeItem(k); } catch { /* storage disabled */ }
};

export const getToken = () => _safeGet(TOKEN_KEY);
export const getStoredUser = () => {
  try { return JSON.parse(_safeGet(USER_KEY) || "null"); } catch { return null; }
};
// Auth now lives in an httpOnly cookie set by the backend. We only cache the
// (non-sensitive) user object for instant paint; any legacy localStorage token
// is removed so the cookie becomes the single source of truth.
export const setSession = (_token, user) => {
  _safeRemove(TOKEN_KEY);
  _safeSet(USER_KEY, JSON.stringify(user));
};
export const clearSession = () => {
  _safeRemove(TOKEN_KEY);
  _safeRemove(USER_KEY);
};

async function request(path, { method = "GET", body, isForm = false } = {}) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload;
  if (isForm) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(`${BASE}/api/portal${path}`, { method, headers, body: payload, credentials: "include" });
  } catch (networkErr) {
    const err = new Error("Connection failed. Please check your internet and try again.");
    err.status = 0;
    err.networkError = true;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  let data;
  try {
    data = ct.includes("application/json") ? await res.json() : await res.text();
  } catch {
    data = null;
  }
  if (!res.ok) {
    // Never leak raw HTML / gateway markup into Error.message — keep messages short and human.
    let detail;
    if (data && typeof data === "object" && data.detail) {
      detail = typeof data.detail === "string" ? data.detail : "Request failed";
    } else if (res.status === 502 || res.status === 503 || res.status === 504) {
      detail = "The server is temporarily unavailable. Please refresh or try again in a moment.";
    } else if (res.status === 401) {
      detail = "Your session has expired. Please sign in again.";
    } else if (res.status === 403) {
      detail = "You do not have permission to perform this action.";
    } else if (res.status === 404) {
      detail = "Not found.";
    } else if (res.status === 413) {
      detail = "File is too large.";
    } else if (res.status === 429) {
      detail = "Too many requests. Please slow down and try again.";
    } else if (typeof data === "string" && data.length < 200 && !/[<>]/.test(data)) {
      detail = data;
    } else {
      detail = `Something went wrong (HTTP ${res.status}). Please refresh or try again.`;
    }
    const err = new Error(detail);
    err.status = res.status;
    // Preserve the raw parsed body so callers can render structured
    // validation errors / warnings (e.g. 422/409 with detail as an object).
    err.detail = data && typeof data === "object" ? data.detail : undefined;
    err.data = data;
    throw err;
  }
  return data;
}

export const portalApi = {
  login: (email, password) => request("/auth/login", { method: "POST", body: { email, password } }),
  me: () => request("/auth/me"),
  changePassword: (current_password, new_password) =>
    request("/auth/change-password", { method: "POST", body: { current_password, new_password } }),
  // Users
  createUser: (data) => request("/users", { method: "POST", body: data }),
  listUsers: (role, includeProjects = false) => {
    const q = new URLSearchParams();
    if (role) q.set("role", role);
    if (includeProjects) q.set("include_projects", "true");
    const qs = q.toString();
    return request(`/users${qs ? `?${qs}` : ""}`);
  },
  updateUser: (id, data) => request(`/users/${id}`, { method: "PATCH", body: data }),
  resetUserPassword: (id, data) => request(`/users/${id}/reset-password`, { method: "POST", body: data }),
  resendInvite: (id, data) => request(`/users/${id}/resend-invite`, { method: "POST", body: data }),
  deleteUser: (id) => request(`/users/${id}`, { method: "DELETE" }),
  // Admin migrations / self-heal
  repair4601Milestones: (force = false) =>
    request(`/admin/migrations/4601-milestones${force ? "?force=true" : ""}`, { method: "POST" }),
  // Projects
  createProject: (data) => request("/projects", { method: "POST", body: data }),
  listProjects: () => request("/projects"),
  getProject: (slug) => request(`/projects/${slug}`),
  updateProject: (slug, data) => request(`/projects/${slug}`, { method: "PATCH", body: data }),
  addMember: (slug, data) => request(`/projects/${slug}/members`, { method: "POST", body: data }),
  removeMember: (slug, userId) => request(`/projects/${slug}/members/${userId}`, { method: "DELETE" }),
  // Updates
  createUpdate: (slug, data) => request(`/projects/${slug}/updates`, { method: "POST", body: data }),
  listUpdates: (slug) => request(`/projects/${slug}/updates`),
  editUpdate: (slug, updateId, data) => request(`/projects/${slug}/updates/${updateId}`, { method: "PATCH", body: data }),
  // Comments
  // Comments
  createComment: (slug, data) => {
    // If files are supplied use the multipart endpoint, else stay on the
    // legacy JSON endpoint so we don't regress integrations that don't
    // upload attachments.
    const files = Array.isArray(data?.files) ? data.files : [];
    if (files.length > 0) {
      const fd = new FormData();
      fd.append("body", data.body || "");
      fd.append("parent_type", data.parent_type || "project");
      if (data.parent_id) fd.append("parent_id", data.parent_id);
      fd.append("visibility", data.visibility || "public");
      files.forEach((f) => fd.append("files", f));
      return request(`/projects/${slug}/comments/upload`, { method: "POST", body: fd, isForm: true });
    }
    const { files: _omit, ...payload } = data || {};
    return request(`/projects/${slug}/comments`, { method: "POST", body: payload });
  },
  listComments: (slug, parent_type, parent_id) => {
    const q = new URLSearchParams();
    if (parent_type) q.set("parent_type", parent_type);
    if (parent_id) q.set("parent_id", parent_id);
    return request(`/projects/${slug}/comments?${q}`);
  },
  // Attachments
  upload: async (slug, files, category = "photo", captions = null) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("category", category);
    if (captions && captions.length) {
      captions.forEach((c) => fd.append("captions", c || ""));
    }
    return request(`/projects/${slug}/attachments`, { method: "POST", body: fd, isForm: true });
  },
  updatePhotoCaption: (id, caption) =>
    request(`/attachments/${id}/caption`, { method: "PATCH", body: { caption } }),
  attachmentUrl: (id) => `${BASE}/api/portal/attachments/${id}`,
  // Reports
  generateReport: (slug, kind, date) => {
    const q = new URLSearchParams({ kind });
    if (date) q.set("date", date);
    return request(`/projects/${slug}/reports/generate?${q}`, { method: "POST" });
  },
  listReports: (slug) => request(`/projects/${slug}/reports`),
  getReport: (slug, id) => request(`/projects/${slug}/reports/${id}`),
  updateReport: (slug, id, data) => request(`/projects/${slug}/reports/${id}`, { method: "PATCH", body: data }),
  sendReport: (slug, id, recipients, opts = {}) =>
    request(`/projects/${slug}/reports/${id}/send`, { method: "POST", body: { recipients, ...opts } }),
  respondReport: (slug, id, body) =>
    request(`/projects/${slug}/reports/${id}/responses`, { method: "POST", body: { body } }),

  // Gantt / Timeline / Programme (Phase 2A.1)
  getTimeline: (slug) => request(`/projects/${slug}/timeline`),
  previewGantt: (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request(`/projects/${slug}/gantt/preview`, { method: "POST", body: fd, isForm: true });
  },
  importGantt: (slug, payload) =>
    request(`/projects/${slug}/gantt/import`, { method: "POST", body: payload }),
  ganttDiff: (slug, payload) =>
    request(`/projects/${slug}/gantt/diff`, { method: "POST", body: payload }),
  importHistory: (slug) => request(`/projects/${slug}/import-history`),
  changeReportUrl: (slug, snapshotId) =>
    `${BASE}/api/portal/projects/${slug}/import-history/${snapshotId}/change-report`,
  restoreImport: (slug, snapshotId) =>
    request(`/projects/${slug}/import-history/${snapshotId}/restore`, { method: "POST" }),
  ganttReplaceSummary: (slug) => request(`/projects/${slug}/gantt/replace-summary`),
  // Commissioning register (Priority 4)
  commissioning: (slug, params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
    const qs = q.toString();
    return request(`/projects/${slug}/commissioning${qs ? `?${qs}` : ""}`);
  },
  commissioningConfig: (slug) => request(`/projects/${slug}/commissioning/config`),
  setCommissioningConfig: (slug, body) =>
    request(`/projects/${slug}/commissioning/config`, { method: "PUT", body }),
  createCommissioning: (slug, body) =>
    request(`/projects/${slug}/commissioning`, { method: "POST", body }),
  updateCommissioning: (slug, id, body) =>
    request(`/projects/${slug}/commissioning/record/${id}`, { method: "PATCH", body }),
  deleteCommissioning: (slug, id) =>
    request(`/projects/${slug}/commissioning/record/${id}`, { method: "DELETE" }),
  progressSummary: (slug) => request(`/projects/${slug}/progress-summary`),
  patchTask: (slug, taskId, data) =>
    request(`/projects/${slug}/tasks/${taskId}`, { method: "PATCH", body: data }),
  deleteTask: (slug, taskId) =>
    request(`/projects/${slug}/tasks/${taskId}`, { method: "DELETE" }),
  patchTimelineItem: (slug, entity, id, data) =>
    request(`/projects/${slug}/${entity}/${id}`, { method: "PATCH", body: data }),
  deleteTimelineItem: (slug, entity, id) =>
    request(`/projects/${slug}/${entity}/${id}`, { method: "DELETE" }),

  // Admin utilities
  adminSummary: () => request(`/admin/summary`),
  getOverview: (slug) => request(`/projects/${slug}/overview`),
  purgeQaData: () => request(`/admin/purge-qa-data?confirm=PURGE-QA`, { method: "POST" }),
  impersonate: (role, project_slug) =>
    request(`/admin/impersonate`, { method: "POST", body: { role, project_slug } }),
  endImpersonation: () => request(`/admin/end-impersonation`, { method: "POST" }),
  // Reports — Phase 2A.2 (Step 1)
  generateReportV2: (slug, { kind, audience = "client", date }) => {
    const q = new URLSearchParams({ kind, audience });
    if (date) q.set("date", date);
    return request(`/projects/${slug}/reports/generate?${q}`, { method: "POST" });
  },
  reportRecipients: (slug, kind = "daily") =>
    request(`/projects/${slug}/reports/recipients?kind=${kind}`),
  approveReport: (slug, id) =>
    request(`/projects/${slug}/reports/${id}/approve`, { method: "POST" }),
  testSendReport: (slug, id) =>
    request(`/projects/${slug}/reports/${id}/test-send`, { method: "POST" }),
  // Milestones — Slice A
  createMilestone: (slug, body) =>
    request(`/projects/${slug}/milestones`, { method: "POST", body }),
  patchMilestone: (slug, id, body) =>
    request(`/projects/${slug}/milestones/${id}`, { method: "PATCH", body }),
  deleteMilestone: (slug, id) =>
    request(`/projects/${slug}/milestones/${id}`, { method: "DELETE" }),
  setNextMilestone: (slug, id) =>
    request(`/projects/${slug}/milestones/${id}/set-next-week`, { method: "POST" }),
  convertTaskToMilestone: (slug, taskId) =>
    request(`/projects/${slug}/tasks/${taskId}/convert-to-milestone`, { method: "POST" }),
  // Milestone Excel import (Phase 2 #4)
  previewMilestonesUpload: (slug, file) => {
    const fd = new FormData(); fd.append("file", file);
    return request(`/projects/${slug}/milestones/preview`, { method: "POST", body: fd, isForm: true });
  },
  importMilestones: (slug, body) =>
    request(`/projects/${slug}/milestones/import`, { method: "POST", body }),
  // Slice C — milestone-linked updates
  getMilestoneUpdates: (slug, milestoneId) =>
    request(`/projects/${slug}/milestones/${milestoneId}/updates`),
  // i18n — persist current user's preferred language
  updateMyLanguage: (language) =>
    request(`/me/language`, { method: "PATCH", body: { language } }),
  // Content translation (updates / comments) — cached server-side
  translateContent: (slug, body) =>
    request(`/projects/${slug}/translate`, { method: "POST", body }),
  // Payment reminders (admin)
  runPaymentReminders: () =>
    request(`/admin/payment-reminders/run`, { method: "POST" }),
  paymentRemindersLog: () => request(`/admin/payment-reminders/log`),
  // Progress corrections (admin)
  recalcProgress: (slug) =>
    request(`/projects/${slug}/progress/recalculate`, { method: "POST" }),
  patchExternalPackage: (slug, id, body) =>
    request(`/projects/${slug}/external_packages/${id}`, { method: "PATCH", body }),
  markApprovalsComplete: (slug, task_ids) =>
    request(`/projects/${slug}/approvals/mark-complete`, { method: "POST", body: { task_ids } }),
  // Approval Tracker (authority / third-party checklist)
  listApprovals: (slug) => request(`/projects/${slug}/approvals`),
  createApproval: (slug, body) =>
    request(`/projects/${slug}/approvals`, { method: "POST", body }),
  patchApproval: (slug, id, body) =>
    request(`/projects/${slug}/approvals/item/${id}`, { method: "PATCH", body }),
  deleteApproval: (slug, id) =>
    request(`/projects/${slug}/approvals/item/${id}`, { method: "DELETE" }),
  seedApprovals: (slug) =>
    request(`/projects/${slug}/approvals/seed-standard`, { method: "POST" }),
  workstreamsDebug: (slug) => request(`/projects/${slug}/workstreams/debug`),
  demolitionSyncStatus: (slug) =>
    request(`/admin/data-fixes/demolition-sync/status?slug=${encodeURIComponent(slug)}`),
  demolitionSyncApply: (slug) =>
    request(`/admin/data-fixes/demolition-sync?slug=${encodeURIComponent(slug)}&target=95`, { method: "POST" }),
  patchMember: (slug, userId, body) =>
    request(`/projects/${slug}/members/${userId}`, { method: "PATCH", body }),
  // Dashboard KPI drill-down lists (Slice A)
  adminSummaryList: (kind) =>
    request(`/admin/summary/list?kind=${encodeURIComponent(kind)}`),
  // Financials (Slice B — admin-only by default)
  getFinancials: (slug) => request(`/projects/${slug}/financials`),
  getFinanceLedger: (slug) => request(`/projects/${slug}/finance-ledger`),
  addFinanceReceipt: (slug, body) =>
    request(`/projects/${slug}/finance-ledger/receipts`, { method: "POST", body }),
  updateFinanceReceipt: (slug, id, body) =>
    request(`/projects/${slug}/finance-ledger/receipts/${id}`, { method: "PATCH", body }),
  voidFinanceReceipt: (slug, id, body) =>
    request(`/projects/${slug}/finance-ledger/receipts/${id}/void`, { method: "POST", body }),
  addValueRevision: (slug, body) =>
    request(`/projects/${slug}/finance-ledger/revisions`, { method: "POST", body }),
  updateValueRevision: (slug, id, body) =>
    request(`/projects/${slug}/finance-ledger/revisions/${id}`, { method: "PATCH", body }),
  seedPalmReceipts: () =>
    request(`/admin/data-fixes/palm-tower-receipts-seed`, { method: "POST" }),
  patchFinancialsSettings: (slug, body) =>
    request(`/projects/${slug}/financials`, { method: "PATCH", body }),
  previewFinancialsUpload: (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request(`/projects/${slug}/financials/preview`, { method: "POST", body: fd, isForm: true });
  },
  importFinancials: (slug, body) =>
    request(`/projects/${slug}/financials/import`, { method: "POST", body }),
  resetFinancials: (slug) =>
    request(`/projects/${slug}/financials/reset`, { method: "POST" }),
  createPaymentMilestone: (slug, body) =>
    request(`/projects/${slug}/financials/payment-milestones`, { method: "POST", body }),
  patchPaymentMilestone: (slug, id, body) =>
    request(`/projects/${slug}/financials/payment-milestones/${id}`, { method: "PATCH", body }),
  deletePaymentMilestone: (slug, id) =>
    request(`/projects/${slug}/financials/payment-milestones/${id}`, { method: "DELETE" }),
  createVariation: (slug, body) =>
    request(`/projects/${slug}/financials/variations`, { method: "POST", body }),
  patchVariation: (slug, id, body) =>
    request(`/projects/${slug}/financials/variations/${id}`, { method: "PATCH", body }),
  archiveVariation: (slug, id) =>
    request(`/projects/${slug}/financials/variations/${id}/archive`, { method: "POST" }),
  restoreVariation: (slug, id) =>
    request(`/projects/${slug}/financials/variations/${id}/restore`, { method: "POST" }),
  patchCreditApplication: (slug, body) =>
    request(`/projects/${slug}/financials/credit-application`, { method: "POST", body }),
  reclassPalmVariation: () =>
    request(`/admin/data-fixes/palm-tower-variation-reclass`, { method: "POST" }),
  getDemolitionView: (slug) => request(`/projects/${slug}/demolition-view`),
  setDemolitionTaskScope: (slug, taskId, body) =>
    request(`/projects/${slug}/demolition-view/tasks/${taskId}`, { method: "POST", body }),
  setDemolitionAssessment: (slug, body) =>
    request(`/projects/${slug}/demolition-view/assessment`, { method: "POST", body }),
  muroojDemolitionScopeFix: (slug, dryRun = true) =>
    request(`/admin/data-fixes/murooj-demolition-scope?slug=${encodeURIComponent(slug)}&dry_run=${dryRun}`, { method: "POST" }),
  patchStageDetail: (slug, id, body) =>
    request(`/projects/${slug}/financials/stage-details/${id}`, { method: "PATCH", body }),
  deleteStageDetail: (slug, id) =>
    request(`/projects/${slug}/financials/stage-details/${id}`, { method: "DELETE" }),
  patchExternalSupply: (slug, id, body) =>
    request(`/projects/${slug}/financials/external-supply/${id}`, { method: "PATCH", body }),
  deleteExternalSupply: (slug, id) =>
    request(`/projects/${slug}/financials/external-supply/${id}`, { method: "DELETE" }),
  adminFinancialsSummary: () => request(`/admin/financials/summary`),
  stageProgressVsValue: (slug) => request(`/projects/${slug}/financials/stage-progress`),
  financialsLinkOptions: (slug) => request(`/projects/${slug}/financials/link-options`),
  generateReportV3: (slug, { kind, audience = "client", date, include_financials = false, language, sections }) => {
    const q = new URLSearchParams({ kind, audience });
    if (date) q.set("date", date);
    if (include_financials) q.set("include_financials", "true");
    if (language) q.set("language", language);
    return request(`/projects/${slug}/reports/generate?${q}`, {
      method: "POST",
      body: sections ? { sections } : {},
    });
  },
  // Automation (Phase 2A.2 Step 2)
  getAutomation: (slug) => request(`/projects/${slug}/automation`),
  patchAutomation: (slug, body) =>
    request(`/projects/${slug}/automation`, { method: "PATCH", body }),
  runAutomationNow: (slug, kind = "daily") =>
    request(`/projects/${slug}/automation/run-now?kind=${kind}`, { method: "POST" }),
  adminAutomationLog: ({ project_id, status, limit = 100 } = {}) => {
    const q = new URLSearchParams();
    if (project_id) q.set("project_id", project_id);
    if (status) q.set("status", status);
    if (limit) q.set("limit", String(limit));
    return request(`/admin/automation/log?${q}`);
  },
  adminAutomationSummary: () => request(`/admin/automation/summary`),
  reportsAwaitingApproval: () => request(`/admin/reports/awaiting-approval`),
  normalizePendingReports: () =>
    request(`/admin/reports/normalize-pending`, { method: "POST" }),
  progressBreakdown: (slug) => request(`/projects/${slug}/progress-breakdown`),
  externalSupplyOptions: (slug) => request(`/projects/${slug}/external-supply-options`),
  // Phase D — vendor package tracking + query log
  getExternalSupplyDetail: (slug, id) =>
    request(`/projects/${slug}/financials/external-supply/${id}`),
  listSupplyQueries: (slug, id) =>
    request(`/projects/${slug}/financials/external-supply/${id}/queries`),
  createSupplyQuery: (slug, id, body) =>
    request(`/projects/${slug}/financials/external-supply/${id}/queries`, { method: "POST", body }),
  patchSupplyQuery: (slug, queryId, body) =>
    request(`/projects/${slug}/financials/supply-queries/${queryId}`, { method: "PATCH", body }),
  deleteSupplyQuery: (slug, queryId) =>
    request(`/projects/${slug}/financials/supply-queries/${queryId}`, { method: "DELETE" }),
  searchSupplyQueries: (slug, { search, status } = {}) => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status) q.set("status", status);
    return request(`/projects/${slug}/supply-queries?${q}`);
  },
  discardReport: (slug, reportId) =>
    request(`/projects/${slug}/reports/${reportId}`, { method: "DELETE" }),
  bulkDiscardReports: (body) =>
    request(`/admin/reports/bulk-discard`, { method: "POST", body }),
  // Key Documents (Final Files)
  getKeyDocuments: (slug) => request(`/projects/${slug}/key-documents`),
  getKeyDocCategories: (slug) => request(`/projects/${slug}/key-documents/categories`),
  uploadKeyDocument: (slug, { file, doc_category, visibility, is_final = true, caption, replaces_file_id }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_category", doc_category);
    if (visibility) fd.append("visibility", visibility);
    fd.append("is_final", is_final ? "true" : "false");
    if (caption) fd.append("caption", caption);
    if (replaces_file_id) fd.append("replaces_file_id", replaces_file_id);
    return request(`/projects/${slug}/key-documents/upload`, { method: "POST", body: fd, isForm: true });
  },
  patchKeyDocument: (fileId, body) =>
    request(`/attachments/${fileId}/document`, { method: "PATCH", body }),
  deleteKeyDocument: (fileId) =>
    request(`/attachments/${fileId}/document`, { method: "DELETE" }),
};

// Compress an image (File or Blob) to ~2MB max, preserving aspect ratio.
export async function compressImage(file, maxBytes = 2 * 1024 * 1024, maxDim = 2400) {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= maxBytes) return file;
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  // Step quality down until under maxBytes (or quality is very low).
  for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", q));
    if (blob && blob.size <= maxBytes) {
      return new File([blob], (file.name || "photo").replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
    }
  }
  const final = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.45));
  return new File([final], (file.name || "photo").replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
}
